import { useState } from "react";
import type { NewsletterSectionContent } from "@site-factory/config";
import { SectionShell } from "../components/SectionShell";
import { SectionTitle, SectionSubtitle } from "../typography/Typography";
import { Card, CardContent } from "../components/Card";
import { Button } from "../components/Button";
import { showToast } from "../components/Toast";

export interface NewsletterSectionProps extends NewsletterSectionContent {
  className?: string;
}

export function NewsletterSection({
  title,
  subtitle,
  placeholder = "Enter your email",
  ctaText = "Subscribe",
  successMessage = "Thanks! You're on the list.",
  emailTo,
  provider = "custom",
  apiEndpoint,
  className,
}: NewsletterSectionProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    // Mailto fallback if no API configured
    if (!apiEndpoint) {
      const mailto = emailTo ? `mailto:${emailTo}?subject=Newsletter signup&body=${encodeURIComponent(email)}` : undefined;
      if (mailto) {
        window.location.href = mailto;
      }
      setStatus("success");
      showToast({ title: "Subscribed", description: successMessage, variant: "success" });
      return;
    }

    try {
      setStatus("loading");
      const res = await fetch(apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, provider }),
      });
      if (!res.ok) throw new Error("Subscription failed");
      setStatus("success");
      showToast({ title: "Subscribed", description: successMessage, variant: "success" });
      setEmail("");
    } catch (err) {
      console.error("Newsletter subscribe error", err);
      setStatus("error");
      showToast({
        title: "Could not subscribe",
        description: "Please try again or contact us directly.",
        variant: "error",
      });
    }
  };

  return (
    <SectionShell className={className} backgroundClassName="bg-surface-alt">
      <div className="max-w-3xl mx-auto text-center space-y-6">
        {title && <SectionTitle>{title}</SectionTitle>}
        {subtitle && <SectionSubtitle className="mt-2">{subtitle}</SectionSubtitle>}

        <Card className="p-6 shadow-sm-2 hover:shadow-md-2 transition border border-[color:var(--color-text,#0f172a)]/12 bg-[var(--color-surface,#fff)] text-[var(--color-text,#0f172a)]">
          <CardContent className="p-0">
            <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4 items-stretch">
              <div className="flex-1">
                <label htmlFor="newsletter-email" className="sr-only">
                  Email
                </label>
                <input
                  id="newsletter-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={placeholder}
                  className="w-full h-12 px-4 rounded-[12px] border border-[color:var(--color-text,#0f172a)]/18 bg-[var(--color-surface-elevated,var(--color-surface,#fff))] text-[var(--color-text,#0f172a)] placeholder:text-[color:var(--color-text,#0f172a)]/55 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="md:w-auto">
                <Button type="submit" loading={status === "loading"} className="w-full md:w-auto" ripple>
                  {ctaText}
                </Button>
              </div>
            </form>
            {provider !== "custom" && (
              <p className="text-xs mt-3 text-left" style={{ color: "var(--color-text,#0f172a)", opacity: 0.7 }}>
                Connected to {provider}. Update `apiEndpoint` for production.
              </p>
            )}
            {status === "success" && (
              <p className="text-sm mt-3" style={{ color: "var(--color-text,#0f172a)", opacity: 0.8 }}>
                {successMessage}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </SectionShell>
  );
}
